/**
 * ChatConversationDropdown — the Flow dock chat title's account-scoped
 * chat-history dropdown (originally U6, bead terra-flow-global-chat-dock-
 * ot2.6; rewired for the multi-session model in U4, bead
 * terra-flow-chat-multisession-gvr.4).
 *
 * Reads `useKeystoneConversations()` (react-query) for server-persisted
 * history AND `useFlowChatSessionList()` (U3's store) for this account's
 * LIVE sessions, merges them (live entry wins on a shared `conversationId`
 * — a live session is always fresher than its own persisted row) into ONE
 * discriminated row list, dedupes by `conversationId`, and sorts by
 * effective date descending ACROSS all three sources — never three
 * separately-sorted buckets concatenated in a fixed order (Wave 2 review
 * finding: that was this file's OWN prior bug — its doc comment already
 * claimed "sorts by effective date descending" while the code didn't).
 * `relativeTime`/`toDateString` are reused from `ConversationList.tsx` —
 * never forked (coding standards §1, §6).
 *
 * Two selection paths, because a live DRAFT session has no `conversationId`
 * yet to select by: `onSelect(conversationId)` for any row that has one
 * (persisted, or a live session that has committed) — rewired (U4) to
 * `focusOrCreateSessionForConversation` upstream, replacing the old
 * `handleSelect`/single-slot wiring; `onSelectDraftSession(sessionId)` for a
 * live draft row.
 *
 * Copy decision: an untitled row reads `"Untitled"` (`title ?? 'Untitled'`)
 * — deliberately distinct from the create row's own label so the two are
 * never ambiguous (see the original bead's "Copy decision to make
 * explicit").
 *
 * This is chrome for the Flow dock (its host), not chat logic — lives under
 * `flow/EnrollQueue/`, not `account-view/keystone/` (coding standards §1).
 */
import { useMemo } from 'react';
import { cn } from '../../../lib/cn';
import { relativeTime, toDateString } from '../../account-view/keystone/ConversationList';
import { useKeystoneConversations } from '../../account-view/keystone/useKeystoneConversations';
import { useKeystoneConversationMeta } from '../../account-view/keystone/useKeystoneConversationMeta';
import { useFlowChatSessionList, type FlowChatSessionSummary } from './FlowChatSessionsProvider';
import type { Conversation } from '@clay-run/keystone-contract';

export interface ChatConversationDropdownProps {
  /** The chat's current account — every row is filtered to this id. */
  accountId: string;
  /** Display name for the header ("Conversations · <name>"); falls back to the raw id. */
  accountName?: string;
  currentConversationId: string | null;
  /** The viewed session's own id — the only way to identify the ACTIVE row
   *  when it's a draft (no `conversationId` to match on yet). Optional so a
   *  caller that never has a live-session concept (none exist outside the
   *  Flow store today) can omit it; the draft row it would have highlighted
   *  simply never does. */
  currentSessionId?: string;
  /** Selects a row that has a real `conversationId` (persisted, or a live
   *  session that has already committed one). */
  onSelect: (id: string) => void;
  /** Selects a live DRAFT session row (no `conversationId` yet). Optional
   *  so a caller with no live sessions for this account (e.g. a future
   *  reuse outside the Flow multi-session store) can omit it. */
  onSelectDraftSession?: (sessionId: string) => void;
  /** "+ New chat with this account" — starts a fresh session for `accountId`. */
  onNew: () => void;
}

function effectiveDateString(conv: Conversation): string | null {
  return toDateString(conv.updatedAt ?? conv.createdAt ?? null);
}

function effectiveTimestamp(conv: Conversation): number {
  const dateStr = effectiveDateString(conv);
  if (!dateStr) return 0;
  const parsed = new Date(dateStr).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** A committed conversation row — either a server-persisted conversation, or
 *  a live session that has already committed a `conversationId` (its title
 *  is resolved the same way — a live commit's title may not have landed in
 *  `useKeystoneConversations()`'s cache yet, so this always resolves its
 *  own title rather than trusting a possibly-stale merged object). */
function ConversationRow({
  conversationId,
  fallbackTitle,
  timestamp,
  isActive,
  onSelect,
}: {
  conversationId: string;
  /** Known title from the persisted list, when this row came from there
   *  (skips the redundant per-row GET). */
  fallbackTitle?: string | null;
  timestamp: number;
  isActive: boolean;
  onSelect: (id: string) => void;
}) {
  const meta = useKeystoneConversationMeta(fallbackTitle === undefined ? conversationId : null);
  const title = fallbackTitle !== undefined ? fallbackTitle : meta.title;
  return (
    <button
      type="button"
      aria-current={isActive ? 'true' : undefined}
      onClick={() => onSelect(conversationId)}
      className={cn(
        'text-left px-3 py-2 text-[13px] flex items-center justify-between gap-2 min-w-0',
        isActive
          ? 'bg-bg-secondary dark:bg-nightshade-800 font-semibold'
          : 'hover:bg-bg-secondary dark:hover:bg-nightshade-800',
      )}
    >
      <span className="truncate text-content-primary dark:text-nightshade-100">
        {title ?? 'Untitled'}
      </span>
      <span className="shrink-0 text-[11px] text-content-tertiary dark:text-nightshade-500">
        {relativeTime(toDateString(new Date(timestamp)))}
      </span>
    </button>
  );
}

/** A live draft session row — no `conversationId` yet, so it selects by
 *  `sessionId` via `onSelectDraftSession` instead of `onSelect`. */
function DraftSessionRow({
  entry,
  isActive,
  onSelect,
}: {
  entry: FlowChatSessionSummary;
  isActive: boolean;
  onSelect: (sessionId: string) => void;
}) {
  return (
    <button
      type="button"
      aria-current={isActive ? 'true' : undefined}
      onClick={() => onSelect(entry.sessionId)}
      className={cn(
        'text-left px-3 py-2 text-[13px] flex items-center justify-between gap-2 min-w-0',
        isActive
          ? 'bg-bg-secondary dark:bg-nightshade-800 font-semibold'
          : 'hover:bg-bg-secondary dark:hover:bg-nightshade-800',
      )}
    >
      <span className="truncate text-content-primary dark:text-nightshade-100">
        {entry.pendingFirstMessage ?? 'New chat'}
      </span>
      <span className="shrink-0 text-[11px] text-content-tertiary dark:text-nightshade-500">
        {relativeTime(toDateString(new Date(entry.lastActivityAt)))}
      </span>
    </button>
  );
}

/** ONE discriminated row shape for the whole merged, sorted list — replaces
 *  the three separately-sorted-then-concatenated arrays this component used
 *  to render (Wave 2 review finding: that contradicted this file's own doc
 *  comment, which already claimed a single descending sort). */
type MergedRow =
  | { kind: 'draft'; timestamp: number; entry: FlowChatSessionSummary }
  | { kind: 'live'; timestamp: number; entry: FlowChatSessionSummary & { conversationId: string } }
  | { kind: 'persisted'; timestamp: number; id: string; title: string | null };

export function ChatConversationDropdown({
  accountId,
  accountName,
  currentConversationId,
  currentSessionId,
  onSelect,
  onSelectDraftSession,
  onNew,
}: ChatConversationDropdownProps) {
  const { data, isError } = useKeystoneConversations();
  const liveSessions = useFlowChatSessionList();

  const liveForAccount = useMemo(
    () => liveSessions.filter((s) => s.accountId === accountId),
    [liveSessions, accountId],
  );
  const liveConversationIds = useMemo(
    () => new Set(liveForAccount.map((s) => s.conversationId).filter((id): id is string => id !== null)),
    [liveForAccount],
  );

  const rows = useMemo<MergedRow[]>(() => {
    const draftRows: MergedRow[] = liveForAccount
      .filter((s) => s.conversationId === null)
      .map((entry) => ({ kind: 'draft' as const, timestamp: entry.lastActivityAt, entry }));

    const liveRows: MergedRow[] = liveForAccount
      .filter((s): s is FlowChatSessionSummary & { conversationId: string } => s.conversationId !== null)
      .map((entry) => ({ kind: 'live' as const, timestamp: entry.lastActivityAt, entry }));

    // Dedupe: a live session that has already committed a conversationId
    // takes priority over the (possibly stale) persisted row for the same id.
    const persistedRows: MergedRow[] = (data ?? [])
      .filter((c) => c.selectedAccountId === accountId && !liveConversationIds.has(c.id))
      .map((c) => ({ kind: 'persisted' as const, id: c.id, title: c.title ?? null, timestamp: effectiveTimestamp(c) }));

    return [...draftRows, ...liveRows, ...persistedRows].sort((a, b) => b.timestamp - a.timestamp);
  }, [liveForAccount, data, accountId, liveConversationIds]);

  return (
    <div
      role="listbox"
      aria-label="Conversation history"
      className="flex flex-col max-h-80 overflow-y-auto bg-white dark:bg-nightshade-900 border border-secondary dark:border-nightshade-700 rounded-lg shadow-lg"
    >
      <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 border-b border-secondary dark:border-nightshade-700">
        Conversations{accountName ? ` · ${accountName}` : ''}
      </div>
      <button
        type="button"
        onClick={onNew}
        className="text-left px-3 py-2 text-[13px] font-medium text-content-action hover:bg-bg-secondary dark:hover:bg-nightshade-800"
      >
        + New chat with this account
      </button>
      {rows.map((row) => {
        if (row.kind === 'draft') {
          return (
            <DraftSessionRow
              key={row.entry.sessionId}
              entry={row.entry}
              isActive={row.entry.sessionId === currentSessionId}
              onSelect={(sessionId) => onSelectDraftSession?.(sessionId)}
            />
          );
        }
        if (row.kind === 'live') {
          return (
            <ConversationRow
              key={row.entry.conversationId}
              conversationId={row.entry.conversationId}
              timestamp={row.timestamp}
              isActive={row.entry.conversationId === currentConversationId}
              onSelect={onSelect}
            />
          );
        }
        return (
          <ConversationRow
            key={row.id}
            conversationId={row.id}
            fallbackTitle={row.title}
            timestamp={row.timestamp}
            isActive={row.id === currentConversationId}
            onSelect={onSelect}
          />
        );
      })}
      {isError && (
        <div className="px-3 py-2 text-[12px] text-content-tertiary dark:text-nightshade-500">
          Couldn&apos;t load conversations
        </div>
      )}
    </div>
  );
}
